import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject} from '@angular/core';
import {DraftsService, PatientDraft} from '../../../core/services/drafts.service';
import {ReportBuilder, EditorReportTemplate} from '@xmedical/core';
import {Subscription} from 'rxjs';
import {CoreService} from '../../../core/services/core.service';
import {ReportControlEditorService} from '@xmedical/report-builder';
import {debounceTime, distinctUntilChanged, filter} from 'rxjs/operators';
import {ControlType} from '@xmedical/core';
import {SelectOption} from '@xmedical/core';
import {DatePipe} from '@angular/common';
import {DraftInteractionBehaviour} from '../../../core/layout/draft-overlay/draft-interaction-behaviour';
import {Report, NewReport, PatientProcedureRepositoryService, PracticePatientProcedureEntry, PracticeReportTemplate} from '@xmedical/core';
import {PatientReportsStore, PatientStore} from '../../../core/state';

@Component({
    selector: 'app-create-report',
    templateUrl: './create-report.component.html',
    styleUrls: ['./create-report.component.scss'],
    standalone: false
})
export class CreateReportComponent implements OnInit, OnDestroy, DraftInteractionBehaviour {

  @Input() draft: PatientDraft;
  report: Report = null;
  templates: PracticeReportTemplate[] = [];
  entries: PracticePatientProcedureEntry[] = [];
  entryOptions: SelectOption[] = [];
  loading = false;
  errorMessage = '';
  private debounceTimeOut = 1500;
  private reportRepositorySubscription: Subscription;
  private reportEditorSubscription: Subscription;

  private reportsStore = inject(PatientReportsStore);
  private patientStore = inject(PatientStore);

  constructor(private draftService: DraftsService,
              private coreService: CoreService,
              private editorService: ReportControlEditorService,
              private patientProcedureRepository: PatientProcedureRepositoryService,
              private datePipe: DatePipe
  ) {
  }
  get saveButtonEnabled(): boolean {
    return !this.showSelection;
  }

  get showSelection(): boolean {
    return !this.report && (!this.draft || this.draft.draftId === 0);
  }

  get reportTypes(): PracticeReportTemplate[] {
    return this.templates;
  }

  get patientId(): number {
    return this.report?.patientId;
  }

  get date(): Date {
    return new Date();
  }

  get hasTemplates(): boolean {
    return this.templates.length > 0;
  }

  get hasErrorMessage(): boolean {
    return this.errorMessage.length > 0;
  }

  get hasProcedure(): boolean {
    return this.draft.draftId !== 0 && this.report?.template?.procedureId !== null && this.report?.template?.procedureId !== 0;
  }

  ngOnInit(): void {
    this.templates = this.coreService.reportTemplates();
    this.reportEditorSubscription = this.editorService.events$
      .pipe(filter(event => event.type === 'value_changed'))
      .pipe(debounceTime(this.debounceTimeOut), distinctUntilChanged())
      .subscribe(() => this.updateReport());
    if (this.draft.draftId !== 0) {
      this.loadReportDetails();
    }
  }

  ngOnDestroy(): void {
    this.reportRepositorySubscription?.unsubscribe();
    this.reportEditorSubscription?.unsubscribe();
  }

  selectProcedureEntry(id: number) {
    const entry = this.entries.find(e => e.id === id);
    this.report.template.rows.forEach(row => {
      row.controls.forEach(control => {
       if (control.type === ControlType.PROCEDURE_FIELD) {
         control.value = entry.fields.find(field => field.position === control.procedureField)?.value ?? '';
       }
      });
    });
    this.updateReport();
  }

  selectTemplate(id: number) {
    const selectedTemplate = this.templates.find(t => t.id === id);
    const newReport = {
      id: id,
      patientId: this.patientStore.current()!.id,
      practiceId: this.coreService.selectedPracticeId(),
      templateId: selectedTemplate.id,
    } as NewReport;
    this.createSelectedReport(newReport);
  }

  onDelete(row: number, col: number) {
    this.report.template.rows[row].controls.splice(col, 1);
    this.updateReport();
  }

  save() {
    this.report.locked = true;
    this.updateReport();
  }

  private createSelectedReport(report: NewReport) {
    this.errorMessage = '';
    this.reportRepositorySubscription = this.reportsStore.createReport(report).subscribe({
      next: id => {
        this.draft.draftId = id;
        this.draft.draftTitle = 'New ' + report.title;
        this.loadReportDetails();
      },
      error: () => {
        this.errorMessage = 'Could not save this report';
      }
    });
  }

  private updateReport() {
    // The editor's debounced value_changed stream can fire this before the
    // report has finished loading (or after it's gone), leaving this.report
    // null. Skip the save rather than letting the repository dereference
    // report.id and crash (Sentry SWIFTCLAIM-1A).
    if (!this.report) {
      return;
    }
    this.errorMessage = '';
    // Cancel any in-flight save before starting a new one. Without this, rapid
    // debounced autosaves can run concurrent PUTs and an earlier (slower)
    // request can land after a newer one, overwriting the latest edits.
    this.reportRepositorySubscription?.unsubscribe();
    this.reportRepositorySubscription = this.reportsStore.update(this.report).subscribe({
      next: () => {
        if (this.report.locked) {
          this.draftService.closeDraft(this.draft.draftId, this.draft.draftType);
        }
      },
      error: () => {
        this.errorMessage = 'Could not save changes to this report';
      }
    });
  }

  private loadReportDetails() {
    this.errorMessage = '';
    this.loading = true;
    this.reportRepositorySubscription = this.reportsStore.load(this.draft.draftId).subscribe(report => {
      report.template = ReportBuilder.fromJson(report.template);
      this.report = report;
      this.loading = false;
      if (this.hasProcedure) {
        this.patientProcedureRepository.loadEntries(this.report.template.procedureId, this.patientStore.current()!.id).subscribe(entries => {
            this.entries = entries.sort((a, b) => a.createdDate < b.createdDate ? 1 : -1);
            this.entryOptions = this.entries.map(e => ({ value: e.id, text: this.datePipe.transform(e.createdDate, 'yyyy-MM-dd') }));
            if (this.report.template.procedureEntryId === 0 && this.entryOptions.length > 0) {
              this.report.template.procedureEntryId = this.entryOptions[0].value as number;
            }
        });
      }
    }, error => {
      console.error(error);
      this.errorMessage = 'Could not load this report';
      this.loading = false;
    });
  }
}
